import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Target, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SharedAssessmentPage = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSharedAssessment();
  }, [assessmentId]);

  const fetchSharedAssessment = async () => {
    try {
      const response = await axios.get(`${API}/assessments/${assessmentId}/share`);
      setAssessment(response.data);
    } catch (error) {
      toast.error('Failed to load shared assessment');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Assessment not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="px-4 md:px-8 lg:px-12 py-6 border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-heading font-bold">O4S.ai</h1>
          </div>
          <Button
            data-testid="get-your-own-btn"
            onClick={() => navigate('/auth')}
            className="btn-primary gap-2"
          >
            Get Your Own Analysis <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </nav>

      {/* Shared Assessment Content */}
      <div className="px-4 md:px-8 lg:px-12 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 mb-12 text-white">
            <h2 className="text-3xl font-heading font-bold mb-4">Shared Career Assessment</h2>
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <p className="text-indigo-200 text-sm">Education Level</p>
                <p className="font-semibold">{assessment.data.education_level}</p>
              </div>
              <div>
                <p className="text-indigo-200 text-sm">Board/Stream</p>
                <p className="font-semibold">{assessment.data.board_stream}</p>
              </div>
              <div>
                <p className="text-indigo-200 text-sm">Subjects</p>
                <p className="font-semibold">{assessment.data.subjects.length} selected</p>
              </div>
              <div>
                <p className="text-indigo-200 text-sm">Interest Areas</p>
                <p className="font-semibold">{assessment.data.interests.length} areas</p>
              </div>
            </div>
          </div>

          <h3 className="text-3xl font-heading font-bold mb-8">Career Paths</h3>
          <div className="grid gap-8">
            {assessment.career_paths.map((path, idx) => (
              <div
                key={path.id}
                className="bg-white rounded-2xl shadow-lg p-8 border-2 border-slate-100"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-4xl font-heading font-bold text-primary">#{idx + 1}</span>
                  <h4 className="text-2xl font-heading font-bold">{path.title}</h4>
                </div>
                <p className="text-slate-600 text-lg mb-6">{path.description}</p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-slate-600">Success Rate</p>
                    <p className="font-bold text-lg text-green-600">{path.success_rate}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Timeline</p>
                    <p className="font-bold text-lg">{path.timeline}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Salary Range</p>
                    <p className="font-bold text-lg">{path.salary_range}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 p-8 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl">
            <h3 className="text-2xl font-heading font-bold mb-4">Want Your Own Personalized Career Path?</h3>
            <p className="text-slate-600 mb-6">Get AI-powered career recommendations tailored to your profile</p>
            <Button
              data-testid="cta-start-assessment-btn"
              onClick={() => navigate('/auth')}
              className="btn-primary h-12 px-8"
            >
              Start Your Assessment <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedAssessmentPage;